import React from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { testimonialByline } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { useTestimonials } from "@/hooks/usePublicContent";

export default function SuccessStories() {
  const items = useTestimonials();

  return (
    <main className="bg-cream">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-testid="stories-hero">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Success Stories</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-ink text-balance">
            Real families. Real reading wins.
          </h1>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed">
            These are some of the parents and kids Jonna has worked with. Names and details shared with permission.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-20" data-testid="stories-grid">
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-ink/15 p-12 text-center text-ink/60" data-testid="stories-empty">
            Jonna is collecting permission-given stories from current families. Check back soon — or
            <Link to="/contact" className="text-terracotta font-medium link-underline ml-1">join the waitlist</Link>
            {" "}to start your child's story.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {items.map((t) => (
              <Card key={t.id} className="rounded-2xl border border-ink/5 bg-white shadow-sm hover:shadow-md transition" data-testid={`story-${t.id}`}>
                <CardContent className="p-8">
                  <Quote size={28} className="text-terracotta" />
                  <p className="mt-5 text-ink/85 leading-relaxed text-lg">&ldquo;{t.quote}&rdquo;</p>
                  <div className="mt-7 pt-5 border-t border-ink/5 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-ink">{t.parent_name}</div>
                      <div className="text-xs text-ink/60" data-testid={`testimonial-byline-${t.id}`}>{testimonialByline(t)}</div>
                    </div>
                    <div className="flex gap-0.5 text-mustard">
                      {Array.from({ length: t.rating || 5 }).map((_, i) => (
                        <Star key={i} size={16} fill="currentColor" stroke="none" />
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-16 rounded-3xl bg-sage-100 p-10 sm:p-14 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-ink text-balance">
            Be the next story.
          </h2>
          <p className="mt-3 text-ink/70 max-w-xl mx-auto">Jonna takes a limited number of new students each season. Get on the waitlist and she'll be in touch.</p>
          <div className="mt-7">
            <Link to="/contact" data-testid="stories-cta">
              <Button className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12 px-7 shadow-md">
                Join the Waitlist <ArrowRight size={18} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
